import { exactInteger } from '@/lib/exact'
import type { Block, Cell, Column, TableBlock } from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Providers › Routing (`pv-routing`), from `GET /v1/admin/routing/pins` and
 * `GET /v1/admin/routing/routes`.
 *
 * # The record existed and nothing read it
 *
 * Migration 102 has written `routing_pins`, `payment_routes` and
 * `payment_route_candidates` since the routing engine landed. The account of
 * why a payment did not route — every rail considered and the tier each was
 * eliminated at — was reachable only from `psql`. go-wallet#894 added the
 * reads; this screen is the surface.
 *
 * # `prefer` and `require` ARE NOT DEGREES OF THE SAME THING
 *
 * A `prefer` pin reorders the candidates and every other rail stays eligible.
 * A `require` pin ELIMINATES every other candidate — so if the named offering
 * is unavailable, over its limit or unhealthy, the payment does not route at
 * all. A require pin is the most common reason a resolution ends
 * `no_eligible_route` with rails that would otherwise have carried it.
 *
 * The spec says it plainly: *they must not render alike.* So they do not share
 * a tone, and the require row states its consequence rather than its name.
 *
 * # `expires_at` IS NOT `effective_to`
 *
 * A pin can lapse without anybody closing it: the row stays open and stops
 * applying. **A pin somebody withdrew and a pin that ran out are different
 * events with different remedies**, and only the pair tells them apart. Both
 * are rendered, never collapsed into one "until" column.
 *
 * `live` is SERVED, computed by the backend from `effective_from`,
 * `effective_to` and `expires_at` together against the served `as_of`. It is
 * never recomputed here — deriving it a third way is how two surfaces come to
 * disagree about whether an override is in force.
 *
 * # The tiers are `routing.Tier`'s own vocabulary, in its own order
 *
 * entitlement, compatibility, standing, price, override. The screen renders
 * the served `failed_tier_name` and never invents a ranking of its own; an
 * unrecognised tier renders as itself rather than being mapped to a neighbour.
 *
 * # `candidates_considered: 0` is a different problem from `6`
 *
 * Zero means the resolution considered nothing at all. Six means it considered
 * six and eliminated every one. Those have different causes and different
 * fixes, so the count is always shown and never rendered as "none eligible".
 *
 * # No price and no margin appears here, and that is a permission not a gap
 *
 * What a rail costs is a buy price behind `offering.buy_price.read`, which is
 * Finance's. This screen answers to `offering.read`, which Ops holds. Where a
 * cost IS carried on a decision it is stated as absent rather than zero: an
 * unpriced route is not a free one.
 *
 * # Read only
 *
 * There is no set-pin and no close-pin at any permission, so no control is
 * drawn and none is drawn disabled — the absence is not one anybody could be
 * granted.
 */

export const ROUTING_PINS_PATH = '/routing/pins'
export const ROUTING_ROUTES_PATH = '/routing/routes'

const SOURCE = 'postgres · routing_pins, payment_routes (GET /v1/admin/routing/pins, /routing/routes)'
const AUTHORITY = 'operational'

/** The resolution order, verbatim from the design brief. Never reordered here. */
const TIER_ORDER: readonly string[] = ['entitlement', 'compatibility', 'standing', 'price', 'override']

interface Pin {
  readonly id: number | null
  readonly scope: string
  readonly customerId: number | null
  readonly methodCode: string
  readonly direction: string
  readonly currency: string
  readonly offeringKey: string
  readonly mode: string
  /** SERVED. Never recomputed from the boundaries. */
  readonly live: boolean | null
  /** The pin's OWN deadline. Not `effective_to`. */
  readonly expiresAt: string
  readonly effectiveFrom: string
  readonly effectiveTo: string
  readonly reason: string
  readonly createdBy: string
  readonly closedBy: string
}

interface Decision {
  readonly decisionRef: string
  readonly customerId: number | null
  readonly direction: string
  readonly outcome: string
  readonly selectedBy: string
  readonly tiebreak: string
  readonly pinId: number | null
  readonly failedTier: number | null
  readonly failedTierName: string
  readonly failedReason: string
  readonly candidatesConsidered: number | null
  readonly decidedAt: string
}

function readPin(raw: unknown): Pin | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const id = int(o['id'])
  if (id === null) return null
  return {
    id,
    scope: str(o['scope']),
    customerId: int(o['customer_id']),
    methodCode: str(o['method_code']),
    direction: str(o['direction']),
    currency: str(o['currency']),
    offeringKey: str(o['offering_key']),
    mode: str(o['mode']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
    expiresAt: str(o['expires_at']),
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    reason: str(o['reason']),
    createdBy: str(o['created_by']),
    closedBy: str(o['closed_by']),
  }
}

function readDecision(raw: unknown): Decision | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const ref = str(o['decision_ref'])
  if (ref === '') return null
  return {
    decisionRef: ref,
    customerId: int(o['customer_id']),
    direction: str(o['direction']),
    outcome: str(o['outcome']),
    selectedBy: str(o['selected_by']),
    tiebreak: str(o['tiebreak']),
    pinId: int(o['pin_id']),
    failedTier: int(o['failed_tier']),
    failedTierName: str(o['failed_tier_name']),
    failedReason: str(o['failed_reason']),
    candidatesConsidered: int(o['candidates_considered']),
    decidedAt: str(o['decided_at']),
  }
}

/**
 * The two modes, rendered by consequence rather than by name.
 *
 * A reader who sees "require" and thinks "a stronger prefer" has the wrong
 * model, and that wrong model is precisely what makes `no_eligible_route`
 * baffling on a payment whose other rails were healthy.
 */
function modeCell(p: Pin): Cell {
  if (p.mode === 'require') {
    return ['require — eliminates every other rail', 'a']
  }
  if (p.mode === 'prefer') {
    return ['prefer — reorders, others stay eligible', 'n']
  }
  return p.mode === '' ? '— not stated' : p.mode
}

/**
 * In force, or not, and WHY not.
 *
 * `live` is served. Where it is false the screen distinguishes the two ways a
 * pin stops applying, because they need different remedies: somebody closed
 * it, or it ran out on its own and the row is still open.
 */
function liveCell(p: Pin): Cell {
  if (p.live === true) return ['in force', 'g']
  if (p.live === null) return '— not served'
  if (p.closedBy !== '') return ['withdrawn', 'n']
  if (p.expiresAt !== '') return ['lapsed — expired, not closed', 'n']
  return ['not in force', 'n']
}

const PIN_COLS: readonly Column[] = [
  ['#', 'm', '80px'],
  ['Who', 'x', 'minmax(150px,1fr)'],
  ['Rail', 'm', 'minmax(140px,1fr)'],
  ['What it does', 'c', 'minmax(240px,1.6fr)'],
  ['In force', 'c', '190px'],
  ['Its own deadline', 'm', '150px'],
  ['Closed', 'm', '130px'],
  ['Set by', 'x', 'minmax(140px,1fr)'],
]

const DECISION_COLS: readonly Column[] = [
  ['Decision', 'm', 'minmax(150px,1fr)'],
  ['Direction', 'c', '110px'],
  ['Outcome', 'c', 'minmax(170px,1.2fr)'],
  ['Failed at', 'c', 'minmax(180px,1.3fr)'],
  ['Considered', 'm', '120px'],
  ['Decided by', 'x', 'minmax(150px,1fr)'],
  ['When', 'm', '150px'],
]

function outcomeCell(d: Decision): Cell {
  if (d.outcome === 'no_eligible_route') {
    return ['did not route', 'r']
  }
  if (d.outcome === '') return '— not stated'
  return [d.outcome, 'g']
}

/**
 * The tier a refusal died at, in `routing.Tier`'s vocabulary.
 *
 * Only present on `no_eligible_route`, and it is the FIRST wall every candidate
 * hit rather than a summary of all of them. A tier this console does not
 * recognise renders as itself — mapping it to a neighbour would report a wall
 * the payment never met.
 */
function failedCell(d: Decision): Cell {
  if (d.outcome !== 'no_eligible_route') return '—'
  if (d.failedTierName === '') {
    return d.failedTier === null ? '— not stated' : `tier ${String(d.failedTier)} — NOT NAMED`
  }
  const known = TIER_ORDER.includes(d.failedTierName)
  const ordinal = d.failedTier === null ? '' : `${String(d.failedTier)} · `
  return [
    known
      ? `${ordinal}${d.failedTierName}`
      : `${ordinal}${d.failedTierName} — tier this console does not recognise`,
    known ? 'a' : 'n',
  ]
}

/**
 * How many rails were weighed.
 *
 * Zero and six are different problems: nothing was considered at all, versus
 * six were considered and every one eliminated. Rendering either as "none
 * eligible" loses the distinction that decides where to look.
 */
function consideredCell(d: Decision): Cell {
  if (d.candidatesConsidered === null) return '— not served'
  if (d.candidatesConsidered === 0) {
    return ['0 — none considered', 'a']
  }
  return String(d.candidatesConsidered)
}

function decidedByCell(d: Decision): Cell {
  if (d.selectedBy === 'pin_require' || d.selectedBy === 'pin_prefer') {
    const which = d.pinId === null ? 'a pin' : `pin ${String(d.pinId)}`
    return `${d.selectedBy} · ${which}`
  }
  if (d.selectedBy === '') return d.tiebreak === '' ? '—' : d.tiebreak
  return d.tiebreak === '' ? d.selectedBy : `${d.selectedBy} · ${d.tiebreak}`
}

export function composeRouting(
  pinsPayload: Record<string, unknown> | null,
  routesPayload: Record<string, unknown> | null,
  readAt: Date,
): ScreenData {
  const pins: Pin[] = []
  let unreadablePins = 0
  const rawPins = pinsPayload !== null && Array.isArray(pinsPayload['pins'])
    ? (pinsPayload['pins'] as readonly unknown[])
    : []
  for (const entry of rawPins) {
    const parsed = readPin(entry)
    if (parsed === null) unreadablePins += 1
    else pins.push(parsed)
  }

  const decisions: Decision[] = []
  let unreadableDecisions = 0
  const rawRoutes = routesPayload !== null && Array.isArray(routesPayload['decisions'])
    ? (routesPayload['decisions'] as readonly unknown[])
    : []
  for (const entry of rawRoutes) {
    const parsed = readDecision(entry)
    if (parsed === null) unreadableDecisions += 1
    else decisions.push(parsed)
  }

  const requiring = pins.filter((p) => p.mode === 'require' && p.live === true).length
  const refusals = decisions.filter((d) => d.outcome === 'no_eligible_route').length

  const blocks: Block[] = []

  if (requiring > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'In force',
      title: requiring === 1
        ? 'One require pin is in force, and it eliminates every other rail for what it matches'
        : `${String(requiring)} require pins are in force, and each eliminates every other rail for what it matches`,
      body: 'A require pin is not a stronger preference. Every other candidate is removed, so if the named offering is unavailable, over its limit or unhealthy, the payment does not route at all — rails that would otherwise have carried it are never reached. This is the most common reason a resolution ends in no eligible route while the estate looks healthy.',
      ref: 'routing_pins · mode = require',
    })
  }

  const pinTable: TableBlock = {
    t: 'table',
    title: 'Manual overrides',
    src: 'ledger',
    facets: false,
    cols: PIN_COLS,
    rows: pins.map((p) => [
      p.id === null ? '—' : String(p.id),
      p.scope === 'customer'
        ? (p.customerId === null ? 'customer — NOT NAMED' : `customer ${String(p.customerId)}`)
        : (p.scope === '' ? '— not stated' : p.scope),
      p.offeringKey === '' ? '— not stated' : `${p.offeringKey} · ${p.methodCode} ${p.direction} ${p.currency}`,
      modeCell(p),
      liveCell(p),
      p.expiresAt === '' ? 'no deadline of its own' : day(p.expiresAt),
      p.effectiveTo === '' ? '—' : day(p.effectiveTo),
      p.createdBy === '' ? '— not recorded' : p.createdBy,
    ]),
  }
  blocks.push(pinTable)

  const decisionTable: TableBlock = {
    t: 'table',
    title: 'Recent resolutions',
    src: 'ledger',
    facets: false,
    cols: DECISION_COLS,
    rows: decisions.map((d) => [
      d.decisionRef,
      d.direction === '' ? '—' : d.direction,
      outcomeCell(d),
      failedCell(d),
      consideredCell(d),
      decidedByCell(d),
      d.decidedAt === '' ? '—' : d.decidedAt,
    ]),
  }
  blocks.push(decisionTable)

  blocks.push({
    t: 'note',
    tone: 'n',
    kicker: 'What this cannot tell you',
    title: 'What a rail costs is not on this screen, and that is a permission rather than a gap',
    body: "A rail's buy price sits behind offering.buy_price.read, which is Finance's; this screen answers to offering.read, which Ops holds. Where a decision does carry the cost of its winning candidate it is stated as absent rather than zero, because an unpriced route is not a free one — and it is neither a tariff nor a margin.",
    ref: 'offering.read · buy prices are offering.buy_price.read',
  })

  const missing: string[] = []
  if (unreadablePins > 0) missing.push(`${String(unreadablePins)} unreadable pin row(s)`)
  if (unreadableDecisions > 0) missing.push(`${String(unreadableDecisions)} unreadable resolution row(s)`)
  if (pinsPayload === null) missing.push('the manual overrides — that read failed, so whether any pin is forcing a rail is unknown from here')
  if (routesPayload === null) missing.push('the recent resolutions — that read failed')
  missing.push('what each rail costs — buy prices are behind offering.buy_price.read')
  missing.push("one resolution's full candidate trace — that is a separate read, opened from a decision")

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  return {
    blocks,
    provenance,
    subject: `routing · ${String(pins.length)} pin(s), ${String(decisions.length)} resolution(s), ${String(refusals)} did not route`,
  }
}

export const loadRouting: ScreenLoader = async (api: ApiClient) => {
  const [pinsPayload, routesPayload] = await Promise.all([
    readOne(api, ROUTING_PINS_PATH),
    readOne(api, ROUTING_ROUTES_PATH),
  ])
  if (pinsPayload === null && routesPayload === null) {
    return {
      blocks: [{
        t: 'note',
        tone: 'r',
        kicker: 'Not read',
        title: 'The routing record could not be read',
        body: 'Neither the overrides nor the recent resolutions could be read. Nothing is drawn. This is NOT an empty estate: whether a pin is forcing every payment onto one rail, and whether resolutions are refusing, are both unknown from here.',
        ref: 'GET /v1/admin/routing/pins, /v1/admin/routing/routes',
      }],
      provenance: { kind: 'unread', source: SOURCE, reason: 'both routing reads failed' },
    }
  }
  return composeRouting(pinsPayload, routesPayload, new Date())
}

async function readOne(api: ApiClient, path: string): Promise<Record<string, unknown> | null> {
  try {
    const payload = await api.get(path)
    return typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : null
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return null
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
